/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { TFilter } from 'utils/context/FilterContext/types';
import { isEmpty, overrideMap } from 'utils/context/FilterContext/utils';
import DK from 'utils/dictionaries/filter_dictionary';

import {
  IShipmentSearchCriteria,
  ShipmentIdentifierFilter,
  ShipmentStopFilter,
} from '@project44-models/shipment-list';

const getTimeUnit = (value: number) => {
  if (value % 43200 === 0) {
    return [value / 43200, 43200];
  }
  if (value % 10080 === 0) {
    return [value / 10080, 10080];
  }
  if (value % 1440 === 0) {
    return [value / 1440, 1440];
  }
  if (value % 60 === 0) {
    return [value / 60, 60];
  }
  if (value) {
    return [value, 1];
  }
  return [1, 1440];
};

// TODO Fix any and remove /dist/ import
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getIdentifiers = (view: any) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return view?.map((identifier: any) => [
    DK[identifier.types[0]],
    DK[identifier.operator],
    identifier.values,
  ]);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getType = (identifier: any) => {
  return (
    identifier &&
    identifier[0] &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    identifier[0]?.types?.map((type: any) => DK[type])
  );
};

function mapLocation(location?: ShipmentStopFilter) {
  if (!location) {
    return {};
  }
  const { properties, identifiers, stopType, scopes } = location;

  const params = [];

  if (properties) {
    params.push(
      ...properties.map((criteria) => [
        DK[overrideMap(criteria.propertyName, scopes)],
        DK[criteria.operator!],
        criteria.values,
      ]),
    );
  }

  if (identifiers) {
    params.push(
      ...identifiers.map((criteria) => [
        criteria.types![0] === 'PORT_UN_LOCODE'
          ? DK[overrideMap('LOCATION_PORT_CODE', scopes)]
          : DK[overrideMap('LOCATION_AIRPORT_CODE', scopes)],
        DK[criteria.operator!],
        criteria.values,
      ]),
    );
  }

  if (stopType) {
    params.push([DK.STOP_TYPE, DK[stopType.operator!], stopType.types]);
  }

  return params;
}

function mapBulkIdentifiers(identifiers?: ShipmentIdentifierFilter[]) {
  if (!identifiers) {
    return [];
  }

  return identifiers?.map((identifier) => {
    return [
      DK[identifier.types![0]],
      DK[identifier.operator!],
      identifier.values,
    ];
  });
}

const eventDict: { [key: string]: string } = {
  LOCATION_ADDRESS: DK.EVENT_ADDRESS,
  LOCATION_AIRPORT_CODE: DK.EVENT_AIRPORT_CODE,
  LOCATION_CITY: DK.EVENT_CITY,
  LOCATION_COUNTRY: DK.EVENT_COUNTRY,
  LOCATION_NAME: DK.EVENT_LOCATION_NAME,
  LOCATION_PORT_CODE: DK.EVENT_PORT_CODE,
  LOCATION_STATE: DK.EVENT_STATE,
  LOCATION_ZIP: DK.EVENT_ZIP,
  OCCURRED_EARLY: DK.ACTUAL_DATETIME,
  OCCURRED_LATE: DK.ACTUAL_DATETIME,
  RUNNING_EARLY: DK.ESTIMATED_DATETIME,
  RUNNING_LATE: DK.ESTIMATED_DATETIME,
};
const mapEvent = (
  shipEvent: NonNullable<IShipmentSearchCriteria['shipmentEvents']>[number],
) => ({
  [DK[shipEvent.type]]: [
    ...(shipEvent.dateRanges || []).map((range) => {
      if (range?.startDateTime && range?.endDateTime) {
        return [
          DK[range.type],
          DK[range.operator!],
          [range.startDateTime, range.endDateTime].map((time) =>
            new Date(time).getTime().toString(),
          ),
        ];
      }
      return [
        DK[range.type],
        range?.offsetMinutes && range.offsetMinutes > 0
          ? DK.IN_THE_NEXT
          : DK.IN_THE_LAST,
        getTimeUnit(Math.abs(range?.offsetMinutes || 1440)).map(String),
      ];
    }),
    ...(shipEvent.deltas || []).map((delta) => [
      eventDict[delta.type],
      ['OCCURRED_LATE', 'RUNNING_LATE'].includes(delta.type)
        ? DK.RUNNING_LATE
        : DK.RUNNING_EARLY,
      getTimeUnit(delta.value).map(String),
    ]),
    ...(shipEvent.stopIdentifiers || []).map((criteria) => [
      criteria.types!.length === 2 ? DK.EVENT_AIRPORT_CODE : DK.EVENT_PORT_CODE,
      DK[criteria.operator!],
      criteria.values,
    ]),
    ...(shipEvent.stopProperties || []).map((search) => [
      eventDict[search.propertyName],
      DK[search.operator!],
      search.values,
    ]),
    shipEvent?.stopType && [
      DK.EVENT_STOP_TYPE,
      DK[shipEvent.stopType.operator!],
      shipEvent.stopType.types,
    ],
  ].filter(Boolean),
});

export const paramsFromShipment = (view: IShipmentSearchCriteria): TFilter => {
  if (!view) {
    return {};
  }

  const bulkIdentifiers = view.shipmentIdentifiers?.filter((id) => !!id.values);

  const [
    destinationLocation,
    originLocation,
    currentLocation,
    anyStop,
    everyStop,
  ] = [
    view.shipmentStops?.find(({ scopes }) => scopes?.includes('DESTINATION')),
    view.shipmentStops?.find(({ scopes }) => scopes?.includes('ORIGIN')),
    view.shipmentStops?.find(({ scopes }) => scopes?.includes('CURRENT')),
    view.shipmentStops?.find(({ scopes }) => scopes?.includes('ANY_STOP')),
    view.shipmentStops?.find(({ scopes }) => scopes?.includes('EVERY_STOP')),
  ];

  const locations = [
    destinationLocation && DK.DESTINATION,
    originLocation && DK.ORIGIN,
    anyStop && DK.ANY_STOP,
    currentLocation && DK.CURRENT_STOP,
    everyStop && DK.ALL_STOP,
  ].filter(Boolean);

  const sortBy = [
    view.sort && view.sort[0] && DK[view.sort[0].fieldName],
    view.sort && view.sort[0] && DK[view.sort[0].direction],
  ].filter(Boolean);

  const properties = [
    view.shipmentAttributes?.length && DK.REFERENCE_KEY,
    view.shipmentCarrierIdentifiers?.length && DK.CARRIER,
    view.shipmentVesselIdentifiers?.length && DK.VESSEL_OR_VEHICLE_ID,
    view.sharingContextTenants?.length && DK.SHARED_VISIBILITY,
    bulkIdentifiers?.length && DK.IDENTIFIER,
  ].filter(Boolean);

  const referenceKey = view.shipmentAttributes?.map((identifier) => [
    identifier.name,
    DK[identifier.operator!],
    identifier.values,
  ]);

  const sharingContextTenants = view.sharingContextTenants?.map(
    (identifier) => {
      const criteria =
        identifier.scopes![0] === 'RECIPIENT'
          ? DK.SHARING_RECIPIENT_TENANT_NAME
          : DK.SHARING_ORIGINATOR_TENANT_NAME;
      return [criteria, DK[identifier.operator!], identifier.names];
    },
  );

  const modeOfShipment =
    view.transportationModes &&
    view.transportationModes[0] &&
    view.transportationModes[0].mode?.map((mode) => DK[mode]);

  const modeOfShipmentScopes =
    view.transportationModes &&
    view.transportationModes[0] &&
    view.transportationModes[0]?.scopes;

  const orderIdentifier = view.orderIdentifiers?.map((identifier) => [
    DK[identifier.type],
    DK[identifier.operator!],
    identifier.values,
  ]);

  const relatedEntityId = [
    (view.orderAdditionalIdentifiers?.length || view.orderTags?.length) &&
      DK.ORDER_TAGS,
    view.orderIdentifiers?.length && DK.ORDER_IDENTIFIER,
    view.orderDateRanges?.length && DK.ORDER_DATE,
    (view.loadItemIdentifiers?.length || view.loadItemDescriptions?.length) &&
      DK.LOAD_ITEM,
  ].filter(Boolean);

  const orderReferenceKey = [
    ...(view.orderAdditionalIdentifiers?.map((identifier) => [
      DK[`ORDER_IDENTIFIER_${identifier.types![0]}`],
      DK[identifier.operator!],
      identifier.values,
    ]) || []),
    ...(view.orderTags?.map((identifier) => [
      DK[`ORDER_TAG_${identifier.types![0]}`],
      DK[identifier.operator!],
      identifier.values,
    ]) || []),
  ];

  const orderDate = [
    ...(view.orderDateRanges?.map((identifier) => {
      if (identifier?.startDateTime && identifier?.endDateTime) {
        const start = new Date(identifier.startDateTime).getTime().toString();
        const end = new Date(identifier.endDateTime).getTime().toString();
        return [DK[identifier.type], DK[identifier.operator!], [start, end]];
      }
      return [
        DK[identifier.type],
        identifier?.offsetMinutes && identifier?.offsetMinutes > 0
          ? DK.IN_THE_NEXT
          : DK.IN_THE_LAST,
        getTimeUnit(Math.abs(identifier?.offsetMinutes || 1440)).map(String),
      ];
    }) || []),
  ];

  const loadItem = [
    ...(getIdentifiers(view.loadItemIdentifiers) || []),
    ...(view.loadItemDescriptions?.map((identifier) => [
      DK.LOAD_ITEM_DESCRIPTION,
      DK[identifier.operator!],
      identifier.values,
    ]) || []),
  ];

  const shipmentEvents = view.shipmentEvents?.reduce(
    (obj, event) => ({ ...obj, ...mapEvent(event) }),
    {},
  );

  const specificIdentifiers = view.shipmentIdentifiers?.filter(
    (id) => !id.values,
  );

  const params = {
    [DK.SEARCH_TEXT]: view.searchText,
    [DK.SORT_BY]: sortBy,
    [DK.PROPERTIES]: properties,
    [DK.REFERENCE_KEY]: referenceKey,
    [DK.CARRIER]: getIdentifiers(view.shipmentCarrierIdentifiers),
    [DK.VESSEL_OR_VEHICLE_ID]: getIdentifiers(view.shipmentVesselIdentifiers),
    [DK.CURRENT_STATE]: getType(view.shipmentStates),
    [DK.CONNECTIVITY]: getType(view.shipmentTrackingStatusEvents),
    [DK.ID_TYPE]: getType(specificIdentifiers),
    [DK.IDENTIFIER]: mapBulkIdentifiers(bulkIdentifiers),
    [DK.MODE_OF_SHIPMENT]: modeOfShipment,
    [DK.MODE_OF_SHIPMENT_SCOPES]: modeOfShipmentScopes,
    [DK.LOCATIONS]: locations,
    [DK.DESTINATION]: mapLocation(destinationLocation),
    [DK.ORIGIN]: mapLocation(originLocation),
    [DK.CURRENT_STOP]: mapLocation(currentLocation),
    [DK.ANY_STOP]: mapLocation(anyStop),
    [DK.ALL_STOP]: mapLocation(everyStop),
    [DK.RELATED_ENTITY_ID]: relatedEntityId,
    [DK.ORDER_TAGS]: orderReferenceKey,
    [DK.ORDER_IDENTIFIER]: orderIdentifier,
    [DK.ORDER_DATE]: orderDate,
    [DK.LOAD_ITEM]: loadItem,
    [DK.EVENTS_AND_DATES]: view.shipmentEvents?.map(({ type }) => DK[type]),
    [DK.SHARED_VISIBILITY]: sharingContextTenants,
    ...shipmentEvents,
  } as TFilter;

  Object.keys(params).forEach((key) => {
    if (isEmpty(params[key])) {
      delete params[key];
    }
  });

  return params;
};
